import { Injectable } from '@angular/core';

import {
  HttpClient,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { EMPTY, throwError, of, Observable } from 'rxjs';
import { finalize, catchError, tap } from 'rxjs/operators';

import { Cache } from '../../services/cache/cache.service';
import { LoaderService } from '../../services/loader/loader.service';
import { Logger } from '../../services/logger/logger.service';
import { MessageService } from '../../services/message/message.service';
import { TrackingService } from '../../services/tracking/tracking.service';

export interface RequestOptionsArgs {
  cache?: boolean;
  loaderText?: string;
  skipLoader?: boolean;
  throwErrors?: boolean;
  operation?: string;
  headers?: HttpHeaders | any; // temporal any;
  params?: { [key: string]: any };
  responseType?: any;
  withCredentials?: boolean;
  excludeHeaders?: boolean; // temporal property
  observe?: any;
}

@Injectable()
export class BackendService {
  constructor(
    private http: HttpClient,
    private trackingService: TrackingService,
    private loader: LoaderService,
    private message: MessageService,
    private cache: Cache,
    private logger: Logger,
  ) { }

  public load(url: string, options: RequestOptionsArgs = {}): Observable<any> {
    if (options.cache) {
      const cached = this.cache.get(JSON.stringify([url, options]));
      if (cached) {
        return of(cached);
      }
    }

    if (!options.skipLoader) {
      this.loader.show(options.loaderText);
    }

    // This is a temporal solution until it is fixed in the backend
    let requestOptions = this.getRequestOptions(options);
    if (options.excludeHeaders) {
      requestOptions.headers = undefined;
    }

    return this.http.get(url, requestOptions).pipe(
      tap(response => {
        if (options.cache) {
          this.cache.put(JSON.stringify([url, options]), response);
        }
      }),
      catchError((response: Response) => {
        return this.handleError(response, options);
      }),
      finalize(() => {
        if (!options.skipLoader) {
          this.loader.hide();
        }
      }),
    );
  }

  public loadCache(url: string, options: RequestOptionsArgs = {}): any {
    let cached;
    if (options.cache) {
      cached = this.cache.get(JSON.stringify([url, options]));
    }
    return cached;
  }

  public create(url: string, body: any, options: RequestOptionsArgs = {}): Observable<any> {
    if (!options.skipLoader) {
      this.loader.show(options.loaderText);
    }

    if (body instanceof FormData) {
      options.headers ||= new HttpHeaders();
    }

    return this.http.post(url, body, this.getRequestOptions(options)).pipe(
      catchError((response: Response) => {
        return this.handleError(response, options);
      }),
      finalize(() => {
        if (!options.skipLoader) {
          this.loader.hide();
        }
      }),
    );
  }

  public update(url: string, body: any, options: RequestOptionsArgs = {}): Observable<any> {
    if (!options.skipLoader) {
      this.loader.show(options.loaderText);
    }

    return this.http.put(url, body, this.getRequestOptions(options)).pipe(
      catchError((response: Response) => {
        return this.handleError(response, options);
      }),
      finalize(() => {
        if (!options.skipLoader) {
          this.loader.hide();
        }
      }),
    );
  }

  public delete(url: string, options: RequestOptionsArgs = {}): Observable<any> {
    if (!options.skipLoader) {
      this.loader.show(options.loaderText);
    }

    return this.http.delete(url, this.getRequestOptions(options)).pipe(
      catchError((response: Response) => {
        return this.handleError(response, options);
      }),
      finalize(() => {
        if (!options.skipLoader) {
          this.loader.hide();
        }
      }),
    );
  }

  public upload(url: string, body: FormData, options: RequestOptionsArgs = {}): Observable<any> {
    if (!options.skipLoader) {
      this.loader.show(options.loaderText);
    }
    options.headers ||= new HttpHeaders();

    return this.http.post(url, body, this.getRequestOptions(options)).pipe(
      catchError((response: Response) => {
        return this.handleError(response, options);
      }),
      finalize(() => {
        if (!options.skipLoader) {
          this.loader.hide();
        }
      }),
    );
  }

  public convertJSONtoParams(json: object): HttpParams {
    let params: HttpParams = new HttpParams();
    for (const key in json) {
      if (json.hasOwnProperty(key) && (json[key] || json[key] === false || json[key] === 0)) {
        if (json[key].constructor === Array && !json[key].length) {
          continue;
        } else if (typeof json[key] === 'object' && !(json[key] instanceof Array)) {
          for (const nestedKey in json[key]) {
            if (json[key].hasOwnProperty(nestedKey) && (json[key][nestedKey] || json[key][nestedKey] === false || json[key][nestedKey] === 0)) {
              params = params.append(`${key}[${nestedKey}]`, String(json[key][nestedKey]));
            }
          }
        } else {
          params = params.append(key, String(json[key]));
        }
      }
    }
    return params;
  }

  private getRequestOptions(options?: RequestOptionsArgs): Partial<RequestOptionsArgs> {
    let defaultHeaders: HttpHeaders = new HttpHeaders();
    defaultHeaders = defaultHeaders.append('Content-Type', 'application/json; charset=UTF-8');

    return {
      headers: options?.headers || defaultHeaders,
      observe: options.observe || 'body',
      params: options?.params ? this.convertJSONtoParams(options.params) : undefined,
      responseType: options?.responseType || 'json',
      withCredentials: options?.withCredentials,
    };
  }

  public handleError(response: Response | any, options: RequestOptionsArgs = {}) {
    if (options.throwErrors) {
      return throwError(response);
    } else {
      const headers = response.headers || new HttpHeaders();
      const xRayId = headers.get('X-Amzn-Trace-Id');
      if (response.status === 401) {
        this.message.unauthorized({ xRayId: xRayId });
        this.trackingService.track('Unauthorized Error', { value: 'backend service' });
      } else if (response.status === 403) {
        const error = response.error.includes('Dataset:') ? response.error : null;
        const customErrorMsg = response.error.includes('Cannot delete') ? response.error : false;
        this.message.forbidden({ xRayId: xRayId, errorInfo: error, customErrorMsg: customErrorMsg });
        this.trackingService.track('Forbidden Error', { value: 'backend service', error: error });
      } else if (response.status === 404) {
        this.message.error(response.error, { xRayId: xRayId });
        this.trackingService.track('Not Found Error', { value: 'backend service' });
      } else if (response.status === 0) {
        const error = 'An Ad Blocker may be causing an issue. We recommend making sure it’s disabled for the burthub.com domain. If this doesn’t resolve the issue please reach out to support@burtcorp.com for help.';
        this.message.error(error, { xRayId: xRayId });
        this.logger.error('Error caught', response);
        this.trackingService.track('No Response Error', { error: error });
      } else if (response.status === 422) {
        this.message.error(response.error);
        this.trackingService.track('Unprocessable Entity Error', { value: 'backend service' });
      } else {
        const contentType = headers.get('Content-Type');
        const isText = (contentType && contentType.includes('text/plain'));
        const error = isText ? response.error : 'Try to reload the page but if the problem persists, please let us know.';

        this.message.error(error, { xRayId: xRayId });
        this.logger.error('Error caught', response);
        this.trackingService.track('Backend Error', { error: error });
      }
      return EMPTY;
    }
  }
}
